import React, { useEffect, useMemo, useState } from "react";
import useUserStore from "../stores/useUserStore";
import useFleetStore from "../stores/useFleetStore";
import { useApi } from "../apiHelpers";
import { useToast } from "../components/Toast/ToastContext";
import { bulkClearFlags } from "../utils/flag-utils";

import { useConfirm } from "../components/ConfirmModal/ConfirmContext";
import { DataTable } from "../components/table/DataTable";
import { Alert } from '../components/ui/Alert';
import { buttonClass } from '../components/ui/Button';
import Dialog from '../components/ui/Dialog';
import EmptyState from '../components/ui/EmptyState';
import { TextArea } from '../components/ui/Field';
import LogoSpinner from '../components/ui/LogoSpinner';
import { StatTile } from '../components/ui/Panel';
// Icons
const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
  </svg>
);

// Columns stay stable across renders: what each row shows reaches the cells through the table's meta.
const COLUMNS = [
  {
    id: "serial",
    header: "Serial",
    accessorFn: (r) => r.id,
    cell: ({ row }) => <span className="font-mono text-primary">{row.original.id}</span>,
  },
  {
    id: "flag",
    header: "Current Flag",
    enableSorting: false,
    cell: ({ row, table }) => {
      const desc = table.options.meta.descriptions[row.original.id];
      const hasFlag = desc !== "None" && desc !== "Not Found";
      return hasFlag ? (
        <span className="text-yellow-400">{desc}</span>
      ) : (
        <span className="text-on-surface-variant italic">{desc}</span>
      );
    },
  },
];

const getRowKey = (r) => String(r.idx);

const DEFAULT_MAINTENANCE_LOG = "Batch Maintenance Approval";

const BulkFlagClear = ({ droneIds, closeModal, onDirtyChange }) => {
  const api = useApi();
  const toast = useToast();
  const confirm = useConfirm();
  const user = useUserStore((state) => state.user);
  const drones = useFleetStore((state) => state.drones);
  const flags = useFleetStore((state) => state.flags);
  const refreshFleet = useFleetStore((state) => state.refreshFleet);
  const [error, setError] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [maintenanceLog, setMaintenanceLog] = useState(DEFAULT_MAINTENANCE_LOG);
  const dirty = maintenanceLog !== DEFAULT_MAINTENANCE_LOG;
  useEffect(() => {
    if (onDirtyChange) onDirtyChange(dirty);
  }, [dirty]); // eslint-disable-line react-hooks/exhaustive-deps

  // When confirmed, clear flags for all drones in the list.
  const handleConfirmClear = async () => {
    // Validate user is logged in
    if (!user?.loggedin) {
      setError("User information unavailable. Please log in again.");
      return;
    }

    // Validate maintenance log
    if (!maintenanceLog.trim()) {
      setError("Maintenance log cannot be empty.");
      return;
    }

    // Filter drones: those in the provided list and not already RTF.
    const dronesToClear = drones.filter(
      (drone) =>
        droneIds.includes(drone.serialnumber) && drone.condition !== "RTF"
    );

    if (dronesToClear.length === 0) {
      setError("No flagged drones to clear in the selected list.");
      return;
    }

    // Confirmation dialog
    const confirmed = await confirm({
      message: `Are you sure you want to clear flags from ${dronesToClear.length} drone${dronesToClear.length !== 1 ? 's' : ''}?`,
    });
    if (!confirmed) return;

    setIsProcessing(true);
    setError("");
    try {
      const serialnumbers = dronesToClear.map(drone => drone.serialnumber);

      // ONE atomic call - all succeed or all fail together (user_id from JWT)
      const result = await bulkClearFlags(api, serialnumbers, maintenanceLog.trim());

      // Refresh fleet data
      refreshFleet();

      toast.success(
        `Successfully cleared ${result.processed} flags and logged maintenance.`
      );
      setShowMaintenanceModal(false);
      closeModal();
    } catch (error) {
      console.error("Bulk flag clear failed:", error);
      setError(error.message || "Bulk flag clear failed. Please try again.");
      // Keep modal open so user sees error
    } finally {
      setIsProcessing(false);
    }
  };

  // Closing the sub-modal puts the note back to its default, so the next opening starts clean.
  const closeMaintenanceModal = () => {
    setShowMaintenanceModal(false);
    setMaintenanceLog(DEFAULT_MAINTENANCE_LOG);
  };

  // Opens the maintenance modal.
  const openMaintenanceModal = () => {
    if (droneIds.length === 0) {
      setError("No drone IDs provided.");
      return;
    }
    setError("");
    setShowMaintenanceModal(true);
  };

  // Retrieve current flag description for a drone.
  const getDroneFlagDescription = (droneSerial) => {
    const drone = drones.find((d) => d.serialnumber === droneSerial);
    if (!drone) return "Not Found";
    const droneFlags = flags.filter(
      (flag) =>
        flag.drone_serial === (drone.serialnumber) && flag.active
    );
    return droneFlags.map((flag) => flag.descript).join(", ") || "None";
  };

  // Count drones with active flags
  const dronesWithFlags = droneIds.filter((id) => getDroneFlagDescription(id) !== "None" && getDroneFlagDescription(id) !== "Not Found");

  const rows = useMemo(() => droneIds.map((id, idx) => ({ id, idx })), [droneIds]);
  const tableMeta = useMemo(
    () => ({ descriptions: Object.fromEntries(droneIds.map((id) => [id, getDroneFlagDescription(id)])) }),
    // getDroneFlagDescription reads `drones` and `flags`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [droneIds, drones, flags],
  );

  return (
    <div className="flex flex-col max-h-[85vh]">
      {/* Header */}
      <div className="px-6 py-4 border-b border-outline-variant bg-green-900/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded bg-green-500/20 flex items-center justify-center text-green-400">
            <CheckIcon />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Bulk Flag Clear</h1>
            <p className="text-on-surface-variant text-sm">{droneIds.length} drone{droneIds.length !== 1 ? 's' : ''} selected</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {droneIds.length > 0 ? (
          <div className="space-y-4">
            {/* Stats */}
            <div className="grid grid-cols-2 gap-3">
              <StatTile on="panel" label="Has Active Flag" value={dronesWithFlags.length} tone="good" />
              <StatTile on="panel" label="Already Clear" value={droneIds.length - dronesWithFlags.length} />
            </div>

            <DataTable
              data={rows}
              columns={COLUMNS}
              meta={tableMeta}
              getRowId={getRowKey}
              on="panel"
              density="compact"
              maxHeight="16rem"
            />

            <Alert tone="error" show={!!error}>{error}</Alert>
          </div>
        ) : (
          <EmptyState variant="area">No drone IDs available.</EmptyState>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-outline-variant bg-surface-container flex justify-end gap-3">
        <button
          onClick={closeModal}
          className={buttonClass('quiet', 'md')}
        >
          Cancel
        </button>
        <button
          onClick={openMaintenanceModal}
          disabled={dronesWithFlags.length === 0 || isProcessing}
          aria-busy={isProcessing}
          className={buttonClass('good', 'md')}
        >
          {isProcessing ? (
            <span className="flex items-center gap-2">
              <LogoSpinner size="inline" />
              Processing...
            </span>
          ) : (
            `Clear ${dronesWithFlags.length} Flag${dronesWithFlags.length !== 1 ? 's' : ''}`
          )}
        </button>
      </div>

      {/* Maintenance Log Sub-Modal */}
      <Dialog
        open={showMaintenanceModal}
        onClose={closeMaintenanceModal}
        dirty={dirty}
        title="Maintenance Log"
        meta="Enter a note for the maintenance record"
        size="md"
        busy={isProcessing}
        problem={error}
        onSubmit={handleConfirmClear}
        footer={
          <>
            <button type="button" onClick={closeMaintenanceModal} className={buttonClass('quiet', 'md')}>
              Cancel
            </button>
            <button
              type="submit"
              disabled={isProcessing || !maintenanceLog.trim()}
              aria-busy={isProcessing}
              className={buttonClass('good', 'md')}
            >
              {isProcessing ? (
                <span className="flex items-center gap-2">
                  <LogoSpinner size="inline" />
                  Processing...
                </span>
              ) : (
                "Confirm & Clear"
              )}
            </button>
          </>
        }
      >
        <TextArea
          value={maintenanceLog}
          onChange={(e) => setMaintenanceLog(e.target.value)}
          placeholder="Enter maintenance log here..."
          aria-label="Maintenance log"
          rows={3}
          maxLength={1000}
        />
      </Dialog>
    </div>
  );
};

export default BulkFlagClear;
